/**
 * System Admin — server, database, logs, jobs, table browser.
 *
 * Plan and decisions: docs/architecture/SYSTEM_ADMIN_SUITE.md
 * Visual direction: frontend/src/styles/system-admin.css
 *
 * Read-only throughout. There is no control on this page that changes the
 * state of the host or the database, and that is deliberate rather than
 * unfinished.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity, Database, FileText, HardDrive, Clock, Table2,
  RefreshCw, Loader2, AlertTriangle, Search, ChevronLeft, ChevronRight, Lock,
  ShieldAlert, BellRing, CheckCircle2, Archive,
} from 'lucide-react';

import { apiRequest } from '../../services/api';
import { Card, CardContent } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Input } from '../../components/new-components/ui/input';
import { Badge } from '../../components/new-components/ui/badge';
import '../../styles/system-admin.css';

type ApiResult<T> = { success: boolean; data: T; error?: string };
const get = <T,>(url: string) => apiRequest('GET', url) as Promise<ApiResult<T>>;

/* ── formatting ───────────────────────────────────────────────────────── */

const num = new Intl.NumberFormat('en-US');

function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v < 1024) return `${v} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let i = -1;
  let x = v;
  do { x /= 1024; i += 1; } while (x >= 1024 && i < units.length - 1);
  return `${x.toFixed(x < 10 ? 1 : 0)} ${units[i]}`;
}

function duration(seconds: number | null | undefined): string {
  if (!seconds && seconds !== 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Thresholds, in one place so every gauge on the page agrees. */
const severity = (percent: number) =>
  (percent >= 90 ? 'sa-crit' : percent >= 75 ? 'sa-warn' : 'sa-ok');

/* ── small presentational pieces ──────────────────────────────────────── */

function Metric({ label, value, sub, tone }: {
  label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: string;
}) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="sa-label">{label}</div>
        <div className={`sa-metric mt-1 ${tone || ''}`}>{value}</div>
        {sub && <div className="sa-sub mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function Gauge({ label, percent, detail }: { label: string; percent: number; detail?: string }) {
  const tone = severity(percent);
  return (
    <Card>
      <CardContent className="py-4">
        <div className="sa-label">{label}</div>
        <div className={`sa-metric mt-1 ${tone}`}>{percent.toFixed(1)}%</div>
        <div className={`sa-bar mt-2 ${tone}`}>
          <span style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
        </div>
        {detail && <div className="sa-sub mt-1.5">{detail}</div>}
      </CardContent>
    </Card>
  );
}

const StateDot = ({ tone }: { tone: string }) => <span className={`sa-dot ${tone}`} />;

function serviceTone(s: { activeState?: string; critical?: boolean }) {
  if (s.activeState === 'active') return 'sa-ok';
  if (s.activeState === 'inactive') return s.critical ? 'sa-crit' : 'sa-idle';
  return 'sa-crit';
}

const LEVEL_TONE: Record<string, string> = {
  ERROR: 'sa-crit', FATAL: 'sa-crit', CRITICAL: 'sa-crit',
  WARN: 'sa-warn', INFO: 'sa-idle', DEBUG: 'sa-idle', TRACE: 'sa-idle',
};

/* ── panels ───────────────────────────────────────────────────────────── */

function OverviewPanel({ data }: { data: any }) {
  if (!data) return null;
  const { host, process: proc, cpu, memory, disk, database, services, status, degradedUnits } = data;

  return (
    <div className="space-y-5">
      {status !== 'HEALTHY' && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive shrink-0" />
          <span>
            <strong>Degraded.</strong> Critical units not active: {degradedUnits.join(', ')}
          </span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Gauge label="CPU" percent={cpu.overall} detail={`load ${cpu.loadAverage.join(' · ')} · ${cpu.physical} threads`} />
        <Gauge
          label="Memory"
          percent={memory.percentUsed}
          detail={`${bytes(memory.availableBytes)} available of ${bytes(memory.totalBytes)}`}
        />
        <Gauge
          label={`Disk ${disk?.mount ?? ''}`}
          percent={disk?.percentUsed ?? 0}
          detail={`${bytes(disk?.availableBytes)} free of ${bytes(disk?.totalBytes)}`}
        />
        <Metric
          label="Database"
          value={bytes(Number(database.size_bytes))}
          sub={`${database.connections} connections · ${database.tables} tables`}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Metric label="Host uptime" value={duration(host.uptimeSeconds)} sub={`${host.hostname} · ${host.platform} ${host.release}`} />
        <Metric label="API uptime" value={duration(proc.uptimeSeconds)} sub={`pid ${proc.pid} · node ${proc.nodeVersion} · ${proc.environment}`} />
        <Metric label="API memory" value={bytes(proc.rssBytes)} sub={`heap ${bytes(proc.heapUsedBytes)} of ${bytes(proc.heapTotalBytes)}`} />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="sa-scroll-x">
            <table>
              <thead>
                <tr>
                  <th>Service</th><th>State</th><th>Enabled</th>
                  <th>Restarts</th><th>Memory</th><th>Active since</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s: any) => (
                  <tr key={s.unit}>
                    <td>
                      <StateDot tone={serviceTone(s)} />
                      {s.label}
                      <div className="sa-sub sa-mono">{s.unit}</div>
                    </td>
                    <td className={serviceTone(s)}>{s.activeState} / {s.subState}</td>
                    <td className="sa-sub">{s.enabled ?? '—'}</td>
                    <td>{s.restarts ?? 0}</td>
                    <td>{s.memoryBytes ? bytes(s.memoryBytes) : '—'}</td>
                    <td className="sa-sub">{s.activeSince || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SystemPanel({ data }: { data: any }) {
  if (!data) return null;
  const { cpu, memory, disk, network, host } = data;

  return (
    <div className="space-y-5">
      <div>
        <div className="sa-label mb-2">CPU · {cpu.model}</div>
        <div className="sa-cores">
          {cpu.cores.map((c: any) => (
            <Card key={c.core}>
              <CardContent className="py-3">
                <div className="sa-sub">Core {c.core}</div>
                <div className={`text-sm font-semibold mt-0.5 sa-num ${severity(c.percent)}`}>
                  {c.percent.toFixed(1)}%
                </div>
                <div className={`sa-bar mt-1.5 ${severity(c.percent)}`}>
                  <span style={{ width: `${c.percent}%` }} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Metric label="Total memory" value={bytes(memory.totalBytes)} sub={`${memory.percentUsed}% used`} />
        <Metric label="Available" value={bytes(memory.availableBytes)} sub="what a new process could claim" />
        <Metric label="Buffers / cache" value={bytes(memory.buffersCachedBytes)} sub="reclaimable by the kernel" />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="sa-scroll-x">
            <table>
              <thead>
                <tr><th>Mount</th><th>Filesystem</th><th>Used</th><th>Free</th><th>Total</th><th>Utilisation</th></tr>
              </thead>
              <tbody>
                {disk.map((d: any) => (
                  <tr key={d.mount}>
                    <td className="sa-mono">{d.mount}</td>
                    <td className="sa-sub sa-mono">{d.filesystem}</td>
                    <td>{bytes(d.usedBytes)}</td>
                    <td>{bytes(d.availableBytes)}</td>
                    <td>{bytes(d.totalBytes)}</td>
                    <td className={severity(d.percentUsed)} style={{ minWidth: 120 }}>
                      {d.percentUsed}%
                      <div className={`sa-bar mt-1 ${severity(d.percentUsed)}`}>
                        <span style={{ width: `${d.percentUsed}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="sa-scroll-x">
            <table>
              <thead>
                <tr><th>Interface</th><th>Received</th><th>Sent</th><th>RX errors</th><th>TX errors</th></tr>
              </thead>
              <tbody>
                {network.map((n: any) => (
                  <tr key={n.interface}>
                    <td className="sa-mono">{n.interface}</td>
                    <td>{bytes(n.rxBytes)}</td>
                    <td>{bytes(n.txBytes)}</td>
                    <td className={n.rxErrors ? 'sa-warn' : ''}>{num.format(n.rxErrors)}</td>
                    <td className={n.txErrors ? 'sa-warn' : ''}>{num.format(n.txErrors)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="sa-sub px-3 py-2">
            Counters are cumulative since boot ({duration(host.uptimeSeconds)} ago), not a rate.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DatabasePanel({ data }: { data: any }) {
  if (!data) return null;
  const {
    database, activity, cacheHitRatio, tables, unusedIndexes,
    longRunningQueries, blockedLocks, vacuumBacklog,
  } = data;

  const cacheTone = cacheHitRatio === null ? '' : cacheHitRatio >= 95 ? 'sa-ok' : cacheHitRatio >= 90 ? 'sa-warn' : 'sa-crit';

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-4">
        <Metric label="Size" value={bytes(Number(database.size_bytes))} sub={database.name} />
        <Metric
          label="Cache hit ratio"
          value={cacheHitRatio === null ? '—' : `${cacheHitRatio}%`}
          tone={cacheTone}
          sub="reads served from shared buffers"
        />
        <Metric
          label="Connections"
          value={activity.reduce((n: number, a: any) => n + a.n, 0)}
          sub={activity.map((a: any) => `${a.n} ${a.state ?? 'unknown'}`).join(' · ')}
        />
        <Metric label="Blocked locks" value={blockedLocks.length} tone={blockedLocks.length ? 'sa-crit' : 'sa-ok'} sub="ungranted lock requests" />
      </div>

      {longRunningQueries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="sa-label px-3 pt-3 pb-2">Queries running over 5 seconds</div>
            <div className="sa-scroll-x">
              <table>
                <thead><tr><th>PID</th><th>State</th><th>Duration</th><th>Wait</th><th>Query</th></tr></thead>
                <tbody>
                  {longRunningQueries.map((q: any) => (
                    <tr key={q.pid}>
                      <td>{q.pid}</td>
                      <td>{q.state}</td>
                      <td className="sa-warn">{q.duration}</td>
                      <td className="sa-sub">{q.wait_event_type ? `${q.wait_event_type}/${q.wait_event}` : '—'}</td>
                      <td className="sa-cell">{q.query}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="sa-label px-3 pt-3 pb-2">Largest tables</div>
          <div className="sa-scroll-x sa-scroll-y" style={{ maxHeight: 420 }}>
            <table>
              <thead>
                <tr>
                  <th>Table</th><th>Live rows</th><th>Dead rows</th>
                  <th>Total size</th><th>Seq scans</th><th>Index scans</th><th>Last autovacuum</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((t: any) => (
                  <tr key={t.table_name}>
                    <td className="sa-mono">{t.table_name}</td>
                    <td>{num.format(Number(t.live_rows))}</td>
                    <td className={Number(t.dead_rows) > 10000 ? 'sa-warn' : ''}>{num.format(Number(t.dead_rows))}</td>
                    <td>{bytes(Number(t.total_bytes))}</td>
                    <td>{num.format(Number(t.seq_scan))}</td>
                    <td>{num.format(Number(t.idx_scan))}</td>
                    <td className="sa-sub">{t.last_autovacuum ? new Date(t.last_autovacuum).toLocaleDateString() : 'never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="p-0">
            <div className="sa-label px-3 pt-3 pb-2">Never-scanned indexes</div>
            {unusedIndexes.length === 0 ? (
              <p className="sa-sub px-3 pb-3">Every index is being used.</p>
            ) : (
              <div className="sa-scroll-x">
                <table>
                  <thead><tr><th>Index</th><th>Table</th><th>Size</th></tr></thead>
                  <tbody>
                    {unusedIndexes.map((i: any) => (
                      <tr key={i.index_name}>
                        <td className="sa-mono">{i.index_name}</td>
                        <td className="sa-sub sa-mono">{i.table_name}</td>
                        <td>{bytes(Number(i.size_bytes))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="sa-sub px-3 py-2">
              An index nothing reads is pure write cost on every insert.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="sa-label px-3 pt-3 pb-2">Vacuum backlog</div>
            {vacuumBacklog.length === 0 ? (
              <p className="sa-sub px-3 pb-3">No table is carrying significant dead tuples.</p>
            ) : (
              <div className="sa-scroll-x">
                <table>
                  <thead><tr><th>Table</th><th>Dead</th><th>Live</th><th>Last autovacuum</th></tr></thead>
                  <tbody>
                    {vacuumBacklog.map((v: any) => (
                      <tr key={v.table_name}>
                        <td className="sa-mono">{v.table_name}</td>
                        <td className="sa-warn">{num.format(Number(v.dead_rows))}</td>
                        <td>{num.format(Number(v.live_rows))}</td>
                        <td className="sa-sub">{v.last_autovacuum ? new Date(v.last_autovacuum).toLocaleString() : 'never'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ── table browser ────────────────────────────────────────────────────── */

function TablesPanel() {
  const [list, setList] = useState<any[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [rows, setRows] = useState<any | null>(null);
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const LIMIT = 50;

  useEffect(() => {
    get<any[]>('/admin/system/tables').then((r) => setList(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (name: string, off: number, search: string) => {
    setBusy(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(off) });
      if (search) params.set('q', search);
      const r = await get<any>(`/admin/system/tables/${name}?${params}`);
      if (!r.success) throw new Error(r.error || 'Could not read that table');
      setRows(r.data);
    } catch (err: any) {
      setRows(null);
      setError(err?.response?.data?.message || err?.message || 'Could not read that table');
    } finally {
      setBusy(false);
    }
  }, []);

  const open = useCallback((name: string) => {
    setSelected(name);
    setOffset(0);
    setQ('');
    load(name, 0, '');
  }, [load]);

  const visible = useMemo(
    () => list.filter((t) => t.table_name.includes(filter.toLowerCase())),
    [list, filter]
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      <Card>
        <CardContent className="p-3 space-y-2">
          <Input
            placeholder="Filter tables"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="sa-scroll-y" style={{ maxHeight: 560 }}>
            {visible.map((t) => (
              <button
                key={t.table_name}
                type="button"
                disabled={!t.browsable}
                onClick={() => open(t.table_name)}
                title={t.browsable ? undefined : 'Holds credentials — not browsable'}
                className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center justify-between gap-2 ${
                  selected === t.table_name ? 'bg-muted' : 'hover:bg-muted/50'
                } ${t.browsable ? '' : 'opacity-45 cursor-not-allowed'}`}
              >
                <span className="sa-mono truncate">{t.table_name}</span>
                <span className="sa-sub shrink-0 flex items-center gap-1">
                  {!t.browsable && <Lock className="w-3 h-3" />}
                  {num.format(Number(t.live_rows))}
                </span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          {!selected ? (
            <p className="sa-sub py-16 text-center">Select a table.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="sa-mono font-semibold">{selected}</span>
                {rows && (
                  <Badge variant={rows.classification === 'PHI' ? 'destructive' : 'secondary'}>
                    {rows.classification}
                  </Badge>
                )}
                <div className="flex-1" />
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-7 h-8 w-52"
                    placeholder="Search visible columns"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { setOffset(0); load(selected, 0, q); }
                    }}
                  />
                </div>
              </div>

              {rows?.classification === 'PHI' && (
                <p className="sa-sub mb-3">
                  Patient identifiers are masked and cannot be revealed here. Use the clinical
                  surfaces, which scope by facility and care assignment.
                </p>
              )}

              {error && <p className="text-sm text-destructive mb-3">{error}</p>}

              {busy ? (
                <div className="flex justify-center py-12"><Loader2 className="w-4 h-4 animate-spin" /></div>
              ) : rows ? (
                <>
                  <div className="sa-scroll-x sa-scroll-y" style={{ maxHeight: 480 }}>
                    <table>
                      <thead>
                        <tr>
                          {rows.columns.map((c: any) => (
                            <th key={c.name} title={`${c.type}${c.nullable ? ' · nullable' : ''}`}>
                              {c.name}
                              {c.masked && <Lock className="w-3 h-3 inline ml-1 opacity-60" />}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.rows.map((r: any, i: number) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <tr key={i}>
                            {rows.columns.map((c: any) => {
                              const v = r[c.name];
                              return (
                                <td key={c.name} className="sa-cell">
                                  {c.masked
                                    ? <span className="sa-masked">masked</span>
                                    : v === null || v === undefined
                                      ? <span className="sa-null">null</span>
                                      : String(v).slice(0, 160)}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <span className="sa-sub">
                      {num.format(rows.offset + 1)}–{num.format(Math.min(rows.offset + rows.limit, rows.total))} of {num.format(rows.total)}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="outline" size="sm"
                        disabled={rows.offset === 0}
                        onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); load(selected, o, q); }}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        disabled={rows.offset + rows.limit >= rows.total}
                        onClick={() => { const o = offset + LIMIT; setOffset(o); load(selected, o, q); }}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── logs ─────────────────────────────────────────────────────────────── */

function LogsPanel() {
  const [sources, setSources] = useState<any[]>([]);
  const [source, setSource] = useState<string>('app-error');
  const [level, setLevel] = useState('ALL');
  const [q, setQ] = useState('');
  const [data, setData] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (key: string, lvl: string, search: string) => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ lines: '400', level: lvl });
      if (search) params.set('q', search);
      const r = await get<any>(`/admin/system/logs/${key}?${params}`);
      setData(r.data);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    get<any[]>('/admin/system/logs/sources').then((r) => setSources(r.data || []));
  }, []);
  useEffect(() => { load(source, level, q); /* eslint-disable-next-line */ }, [source, level]);

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const s of sources) (g[s.group] ||= []).push(s);
    return g;
  }, [sources]);

  return (
    <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
      <Card>
        <CardContent className="p-3 space-y-3">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <div className="sa-label mb-1">{group}</div>
              {items.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSource(s.key)}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm ${
                    source === s.key ? 'bg-muted' : 'hover:bg-muted/50'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
            >
              {['ALL', 'ERROR', 'WARN', 'INFO', 'DEBUG'].map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <div className="relative flex-1 min-w-40">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-7 h-8"
                placeholder="Search this log"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') load(source, level, q); }}
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => load(source, level, q)} disabled={busy}>
              <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {data && !data.available && (
            <p className="text-sm sa-warn mb-2">Unavailable — {data.reason}</p>
          )}
          {data && (
            <p className="sa-sub mb-2">
              {num.format(data.entries.length)} shown of {num.format(data.scanned)} lines read.
              Secrets are redacted before the log leaves the server.
            </p>
          )}

          <div className="sa-scroll-y sa-scroll-x" style={{ maxHeight: 560 }}>
            <table className="sa-log">
              <thead><tr><th style={{ width: 150 }}>Time</th><th style={{ width: 66 }}>Level</th><th>Message</th></tr></thead>
              <tbody>
                {data?.entries.map((e: any, i: number) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i}>
                    <td className="sa-time sa-mono">{e.timestamp || '—'}</td>
                    <td className={LEVEL_TONE[e.level] || 'sa-idle'}>{e.level}</td>
                    <td className="sa-msg">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── jobs ─────────────────────────────────────────────────────────────── */

function JobsPanel({ data }: { data: any }) {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <div className="sa-label px-3 pt-3 pb-2">Timers</div>
          <div className="sa-scroll-x">
            <table>
              <thead><tr><th>Timer</th><th>State</th><th>Last run</th><th>Next run</th></tr></thead>
              <tbody>
                {data.timers.map((t: any) => (
                  <tr key={t.unit}>
                    <td><StateDot tone={serviceTone(t)} />{t.label}</td>
                    <td className={serviceTone(t)}>{t.activeState} / {t.subState}</td>
                    <td className="sa-sub">{t.lastTrigger || '—'}</td>
                    <td className="sa-sub">{t.nextElapse || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="sa-label px-3 pt-3 pb-2">Jobs</div>
          <div className="sa-scroll-x">
            <table>
              <thead><tr><th>Job</th><th>State</th><th>Last result</th><th>Last active</th></tr></thead>
              <tbody>
                {data.jobs.map((j: any) => (
                  <tr key={j.unit}>
                    <td><StateDot tone={serviceTone(j)} />{j.label}</td>
                    <td className="sa-sub">{j.activeState} / {j.subState}</td>
                    <td className={j.result && j.result !== 'success' ? 'sa-crit' : 'sa-ok'}>{j.result || '—'}</td>
                    <td className="sa-sub">{j.activeSince || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="sa-sub px-3 py-2">
            Execution output lives in the journal — see Logs for {data.logSources.map((s: any) => s.label).join(', ')}.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── alerts (Phase 3) ─────────────────────────────────────────────────── */

const ALERT_TONE: Record<string, string> = { CRITICAL: 'sa-crit', WARN: 'sa-warn', OK: 'sa-ok' };

function AlertsPanel({ data }: { data: any }) {
  if (!data) return null;
  const { status, calibrated, thresholdVersion, checks, alerts: fired, counts } = data;

  return (
    <div className="space-y-5">
      {!calibrated && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
          <span>
            <strong>Thresholds are provisional.</strong> They were reasoned rather than measured —
            the plan gated this on a fortnight of real use, which has not happened yet. The observed
            column below is what they should be calibrated against. Config version {thresholdVersion}.
          </span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Metric label="Status" value={status} tone={ALERT_TONE[status]} sub="worst individual check" />
        <Metric label="Critical" value={counts.critical} tone={counts.critical ? 'sa-crit' : ''} />
        <Metric label="Warning" value={counts.warn} tone={counts.warn ? 'sa-warn' : ''} />
        <Metric label="Not measured" value={counts.unobserved} sub="reported, not counted as passing" />
      </div>

      {fired.length === 0 && (
        <div className="flex items-center gap-2 text-sm sa-ok">
          <CheckCircle2 className="w-4 h-4" /> Every measured check is within threshold.
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="sa-scroll-x">
            <table>
              <thead>
                <tr>
                  <th>Check</th><th>Observed</th><th>Warn</th><th>Critical</th>
                  <th>State</th><th>Why this threshold</th>
                </tr>
              </thead>
              <tbody>
                {/* Failures first, then everything else — but everything is
                    shown, because a page that renders nothing when healthy
                    gives no confidence it looked. */}
                {[...checks].sort((a: any, b: any) => {
                  const rank: Record<string, number> = { CRITICAL: 0, WARN: 1, OK: 2 };
                  return rank[a.severity] - rank[b.severity];
                }).map((c: any) => (
                  <tr key={c.key}>
                    <td>
                      <StateDot tone={c.unobserved ? 'sa-idle' : ALERT_TONE[c.severity]} />
                      {c.label}
                      {c.window && <span className="sa-sub"> · {c.window}</span>}
                    </td>
                    <td className={c.unobserved ? 'sa-idle' : ALERT_TONE[c.severity]}>
                      {c.unobserved ? 'not measured' : `${c.observed}${c.unit || ''}`}
                    </td>
                    <td className="sa-sub">{c.warn ?? '—'}{c.unit || ''}</td>
                    <td className="sa-sub">{c.critical ?? '—'}{c.unit || ''}</td>
                    <td className={c.unobserved ? 'sa-idle' : ALERT_TONE[c.severity]}>
                      {c.unobserved ? 'n/a' : c.severity}
                      {c.direction === 'below' && <span className="sa-sub"> (lower is worse)</span>}
                    </td>
                    <td className="sa-sub">{c.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── security (Phase 2) ───────────────────────────────────────────────── */

function SecurityPanel({ data }: { data: any }) {
  if (!data) return null;
  const {
    windowHours, requests, authFailures, topErrorSources,
    threats, threatSummary, adminActions, activationEvents,
  } = data;

  const errTone = requests.errorRatePercent >= 10 ? 'sa-crit'
    : requests.errorRatePercent >= 2 ? 'sa-warn' : 'sa-ok';

  return (
    <div className="space-y-5">
      <p className="sa-sub">
        Last {windowHours} hours. Read-only — blocking and firewall rules live in Iron Dome,
        which owns those actions and their authorization.
      </p>

      <div className="grid gap-4 md:grid-cols-4">
        <Metric label="Requests" value={num.format(requests.total)} sub={`avg ${requests.avg_response_ms ?? '—'} ms`} />
        <Metric
          label="5xx rate"
          value={`${requests.errorRatePercent}%`}
          tone={errTone}
          sub={`${num.format(requests.server_errors)} server · ${num.format(requests.client_errors)} client`}
        />
        <Metric
          label="Auth failures"
          value={num.format(authFailures.n)}
          tone={authFailures.n > 25 ? 'sa-warn' : ''}
          sub={`${authFailures.distinct_sources} distinct sources`}
        />
        <Metric
          label="Unresolved threats"
          value={threatSummary.reduce((n: number, t: any) => n + t.unresolved, 0)}
          tone={threatSummary.some((t: any) => t.unresolved) ? 'sa-warn' : 'sa-ok'}
          sub={`${threats.length} recorded`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="p-0">
            <div className="sa-label px-3 pt-3 pb-2">Response codes</div>
            <div className="sa-scroll-x">
              <table>
                <thead><tr><th>Status</th><th>Count</th><th>Share</th></tr></thead>
                <tbody>
                  {requests.statusMix.map((s: any) => (
                    <tr key={s.status_code}>
                      <td className={s.status_code >= 500 ? 'sa-crit' : s.status_code >= 400 ? 'sa-warn' : 'sa-ok'}>
                        {s.status_code}
                      </td>
                      <td>{num.format(s.n)}</td>
                      <td className="sa-sub">
                        {requests.total ? ((s.n / requests.total) * 100).toFixed(1) : '0'}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="sa-label px-3 pt-3 pb-2">Sources generating errors</div>
            {topErrorSources.length === 0 ? (
              <p className="sa-sub px-3 pb-3">No error responses in this window.</p>
            ) : (
              <div className="sa-scroll-x">
                <table>
                  <thead><tr><th>Source</th><th>Errors</th><th>Last seen</th></tr></thead>
                  <tbody>
                    {topErrorSources.map((s: any) => (
                      <tr key={s.ip_address}>
                        <td className="sa-mono">{s.ip_address}</td>
                        <td>{num.format(s.errors)}</td>
                        <td className="sa-sub">{new Date(s.last_seen).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="sa-sub px-3 py-2">
              Grouped by source — the question is whether one client is misbehaving,
              not what any individual browsed.
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="sa-label px-3 pt-3 pb-2">Threats</div>
          {threats.length === 0 ? (
            <p className="sa-sub px-3 pb-3">Iron Dome has recorded no threats.</p>
          ) : (
            <div className="sa-scroll-x sa-scroll-y" style={{ maxHeight: 320 }}>
              <table>
                <thead>
                  <tr>
                    <th>Time</th><th>Type</th><th>Severity</th><th>Source</th>
                    <th>Request</th><th>Blocked</th><th>Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {threats.map((t: any) => (
                    <tr key={t.id}>
                      <td className="sa-sub">{new Date(t.timestamp).toLocaleString()}</td>
                      <td>{t.threat_type}</td>
                      <td className={/high|critical/i.test(t.severity || '') ? 'sa-crit' : 'sa-warn'}>
                        {t.severity}
                      </td>
                      <td className="sa-mono">{t.ip_address}{t.country_code ? ` · ${t.country_code}` : ''}</td>
                      <td className="sa-cell">{t.request_method} {t.request_path}</td>
                      <td className={t.blocked ? 'sa-ok' : 'sa-warn'}>{t.blocked ? 'yes' : 'no'}</td>
                      <td className={t.resolved ? 'sa-ok' : 'sa-warn'}>{t.resolved ? 'yes' : 'no'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="p-0">
            <div className="sa-label px-3 pt-3 pb-2">Administrative actions</div>
            {adminActions.length === 0 ? (
              <p className="sa-sub px-3 pb-3">No recorded administrative actions.</p>
            ) : (
              <div className="sa-scroll-y" style={{ maxHeight: 280 }}>
                <table>
                  <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Target</th></tr></thead>
                  <tbody>
                    {adminActions.map((a: any) => (
                      <tr key={a.id}>
                        <td className="sa-sub">{new Date(a.created_at).toLocaleString()}</td>
                        <td>{a.admin_email || '—'}</td>
                        <td className="sa-mono">{a.action}</td>
                        <td className="sa-sub sa-mono">{a.target_type || ''} {a.target_id || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="sa-sub px-3 py-2">
              Before/after row contents are deliberately not shown — for a patient edit
              that would be the record itself.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="sa-label px-3 pt-3 pb-2">Account activation</div>
            {activationEvents.length === 0 ? (
              <p className="sa-sub px-3 pb-3">No activation events.</p>
            ) : (
              <div className="sa-scroll-y" style={{ maxHeight: 280 }}>
                <table>
                  <thead><tr><th>When</th><th>Event</th><th>Transition</th><th>Account</th></tr></thead>
                  <tbody>
                    {activationEvents.map((e: any) => (
                      <tr key={e.id}>
                        <td className="sa-sub">{new Date(e.created_at).toLocaleString()}</td>
                        <td>{e.event}</td>
                        <td className="sa-sub">{e.from_state || '—'} → {e.to_state || '—'}</td>
                        <td className="sa-sub">{e.subject_email || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ── retention (Phase 3) ──────────────────────────────────────────────── */

function RetentionPanel({ data }: { data: any }) {
  if (!data) return null;
  const { totalMb, rotatedFiles, prunableFiles, prunableBytes, policy, files, directory } = data;

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-4">
        <Metric label="Log directory" value={`${totalMb} MB`} sub={directory} />
        <Metric label="Rotated files" value={rotatedFiles} sub={`keeping ${policy.max_rotated_files_per_level} per level`} />
        <Metric label="Prunable now" value={prunableFiles} sub={bytes(prunableBytes)} />
        <Metric label="Age limit" value={`${policy.rotated_app_logs_days}d`} sub="rotated files older than this are removed" />
      </div>

      <div className="flex items-start gap-2 rounded-md border px-4 py-3 text-sm">
        <Archive className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
        <span className="sa-sub">
          Pruning runs inside the logger's rotation pass, not from this page. There is no
          delete button: a control that removes files from the production host is exactly
          the state change this suite does not offer. {policy.note}
        </span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="sa-label px-3 pt-3 pb-2">Rotated files</div>
          {files.length === 0 ? (
            <p className="sa-sub px-3 pb-3">
              Nothing has rotated yet — the live logs are still under the 10 MB rotation size.
            </p>
          ) : (
            <div className="sa-scroll-y" style={{ maxHeight: 400 }}>
              <table>
                <thead><tr><th>File</th><th>Size</th><th>Age</th><th>Modified</th></tr></thead>
                <tbody>
                  {files.map((f: any) => (
                    <tr key={f.name}>
                      <td className="sa-mono">{f.name}</td>
                      <td>{bytes(f.sizeBytes)}</td>
                      <td className={f.ageDays > policy.rotated_app_logs_days ? 'sa-warn' : ''}>
                        {f.ageDays}d
                      </td>
                      <td className="sa-sub">{new Date(f.modifiedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── shell ────────────────────────────────────────────────────────────── */

const TABS = [
  { key: 'overview', label: 'Overview', icon: Activity, endpoint: '/admin/system/overview' },
  { key: 'alerts', label: 'Alerts', icon: BellRing, endpoint: '/admin/system/alerts' },
  { key: 'security', label: 'Security', icon: ShieldAlert, endpoint: '/admin/system/security' },
  { key: 'system', label: 'System', icon: HardDrive, endpoint: '/admin/system/system' },
  { key: 'database', label: 'Database', icon: Database, endpoint: '/admin/system/database' },
  { key: 'tables', label: 'Tables', icon: Table2, endpoint: null },
  { key: 'logs', label: 'Logs', icon: FileText, endpoint: null },
  { key: 'jobs', label: 'Jobs', icon: Clock, endpoint: '/admin/system/jobs' },
  { key: 'retention', label: 'Retention', icon: Archive, endpoint: '/admin/system/retention' },
] as const;

type TabKey = typeof TABS[number]['key'];

export default function SystemAdmin() {
  const [tab, setTab] = useState<TabKey>('overview');
  const [payload, setPayload] = useState<Partial<Record<TabKey, any>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const active = TABS.find((t) => t.key === tab)!;

  const load = useCallback(async (key: TabKey) => {
    const def = TABS.find((t) => t.key === key)!;
    if (!def.endpoint) return;
    setBusy(true);
    setError(null);
    try {
      const r = await get<any>(def.endpoint);
      setPayload((p) => ({ ...p, [key]: r.data }));
      setFetchedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  // Live refresh only applies to the panels that poll an endpoint; the table
  // browser and log viewer own their own fetching and must not be yanked out
  // from under someone mid-read.
  useEffect(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (live && active.endpoint) {
      timer.current = setInterval(() => load(tab), 10000);
    }
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [live, tab, active.endpoint, load]);

  return (
    <div className="sysadmin space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">System Admin</h1>
          <p className="sa-sub mt-1">
            Server, database, logs and scheduled work. Read-only — nothing here changes system state.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {fetchedAt && <span className="sa-sub">Updated {fetchedAt.toLocaleTimeString()}</span>}
          <Button
            variant={live ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLive((v) => !v)}
            disabled={!active.endpoint}
            title={active.endpoint ? 'Refresh every 10 seconds' : 'Not applicable on this tab'}
          >
            <Activity className="w-4 h-4 mr-2" />{live ? 'Live' : 'Paused'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => load(tab)} disabled={busy || !active.endpoint}>
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="sa-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className="sa-tab"
            onClick={() => setTab(t.key)}
          >
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {busy && !payload[tab] ? (
        <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <>
          {tab === 'overview' && <OverviewPanel data={payload.overview} />}
          {tab === 'alerts' && <AlertsPanel data={payload.alerts} />}
          {tab === 'security' && <SecurityPanel data={payload.security} />}
          {tab === 'retention' && <RetentionPanel data={payload.retention} />}
          {tab === 'system' && <SystemPanel data={payload.system} />}
          {tab === 'database' && <DatabasePanel data={payload.database} />}
          {tab === 'tables' && <TablesPanel />}
          {tab === 'logs' && <LogsPanel />}
          {tab === 'jobs' && <JobsPanel data={payload.jobs} />}
        </>
      )}
    </div>
  );
}
